import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveLinkProps, isColorDark } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function LoyaltyProgramSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'horizontal';
    const tiers = (content.tiers || []).slice().sort((a, b) => (a.min_points || 0) - (b.min_points || 0));
    const earnRules = content.earn_rules || [];

    if (tiers.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const renderBenefits = (benefits = []) => (
        <ul className="space-y-1.5 mt-3">
            {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                    <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{b}</span>
                </li>
            ))}
        </ul>
    );

    const ctaLink = content.cta_link ? resolveLinkProps(content.cta_link) : null;

    // ── Horizontal variant ──
    if (variant === 'horizontal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    {/* Tiers in horizontal layout */}
                    <div className="relative">
                        {/* Connecting line */}
                        <div className="hidden md:block absolute top-16 left-0 right-0 h-0.5" style={{ background: `linear-gradient(to right, ${hexToRgba(s.accent, 0.1)}, ${hexToRgba(s.accent, 0.3)}, ${hexToRgba(s.accent, 0.1)})` }} />

                        <div className={`grid grid-cols-1 ${tiers.length === 2 ? 'md:grid-cols-2' : tiers.length === 3 ? 'md:grid-cols-3' : tiers.length >= 4 ? 'md:grid-cols-4' : ''} gap-6`}>
                            {tiers.map((tier, i) => {
                                const tierColor = tier.color || s.accent;
                                const isFirst = i === 0;
                                return (
                                    <div key={i}
                                        className={`${card} relative overflow-hidden transition-all duration-700 hover:-translate-y-1 ${isFirst ? 'ring-2 shadow-lg' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                        style={{
                                            transitionDelay: visible ? `${i * 120}ms` : '0ms',
                                            ...(isFirst ? { ringColor: tierColor, boxShadow: `0 0 20px ${hexToRgba(tierColor, 0.2)}` } : {}),
                                        }}
                                    >
                                        {/* Top stripe */}
                                        <div className="h-1.5" style={{ backgroundColor: tierColor }} />
                                        <div className="p-5">
                                            <div className="flex items-center gap-3 mb-3">
                                                {tier.icon && <span className="text-2xl">{tier.icon}</span>}
                                                <div>
                                                    <h4 className="font-bold text-base" style={{ color: s.text }}>{tier.name}</h4>
                                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(tierColor, 0.12), color: tierColor }}>
                                                        {tier.min_points || 0}+ pts
                                                    </span>
                                                </div>
                                            </div>
                                            {renderBenefits(tier.benefits || [])}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Arrow connectors (desktop) */}
                        {tiers.length > 1 && (
                            <div className="hidden md:flex absolute top-14 left-0 right-0 justify-around pointer-events-none" style={{ paddingLeft: `${100 / tiers.length / 2}%`, paddingRight: `${100 / tiers.length / 2}%` }}>
                                {tiers.slice(0, -1).map((_, i) => (
                                    <svg key={i} className="w-6 h-6" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Earn Rules */}
                    {earnRules.length > 0 && (
                        <div className={`mt-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: visible ? `${tiers.length * 120 + 200}ms` : '0ms' }}>
                            <h3 className="text-lg font-bold text-center mb-6" style={{ color: s.text }}>
                                {content.earn_title || 'Como ganar puntos'}
                            </h3>
                            <div className={`grid grid-cols-1 sm:grid-cols-2 ${earnRules.length >= 3 ? 'lg:grid-cols-3' : ''} gap-4 max-w-3xl mx-auto`}>
                                {earnRules.map((rule, i) => (
                                    <div key={i} className="flex items-center gap-3 p-4 rounded-xl" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                            <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium" style={{ color: s.text }}>{rule.action}</span>
                                        </div>
                                        <span className="shrink-0 text-sm font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                            +{rule.points}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CTA */}
                    {content.cta_text && ctaLink && (
                        <div className={`text-center mt-10 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            <a {...ctaLink} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 shadow-lg hover:shadow-xl" style={{ backgroundColor: s.accent }}>
                                {content.cta_text}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                            </a>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Vertical variant ──
    if (variant === 'vertical') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    <div className="relative max-w-2xl mx-auto">
                        {/* Vertical progress line */}
                        <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5" style={{ backgroundColor: hexToRgba(s.accent, 0.15) }} />

                        <div className="space-y-6">
                            {tiers.map((tier, i) => {
                                const tierColor = tier.color || s.accent;
                                const isFirst = i === 0;
                                return (
                                    <div key={i}
                                        className={`relative pl-16 md:pl-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                        style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}
                                    >
                                        {/* Circle on line */}
                                        <div className={`absolute left-3 md:left-5 w-7 h-7 rounded-full flex items-center justify-center text-sm border-2 z-10 ${isFirst ? 'shadow-lg' : ''}`}
                                            style={{
                                                backgroundColor: isFirst ? tierColor : (s.dark ? '#1f2937' : '#ffffff'),
                                                borderColor: tierColor,
                                                color: isFirst ? (isColorDark(tierColor) ? '#fff' : '#111') : tierColor,
                                                ...(isFirst ? { boxShadow: `0 0 12px ${hexToRgba(tierColor, 0.4)}` } : {}),
                                            }}
                                        >
                                            {tier.icon || (i + 1)}
                                        </div>

                                        <div className={`${card} p-5 transition-all hover:-translate-y-0.5 ${isFirst ? 'ring-1' : ''}`}
                                            style={{
                                                borderLeft: `3px solid ${tierColor}`,
                                                ...(isFirst ? { ringColor: hexToRgba(tierColor, 0.3) } : {}),
                                            }}
                                        >
                                            <div className="flex items-center gap-3 mb-2">
                                                <h4 className="font-bold text-base" style={{ color: s.text }}>{tier.name}</h4>
                                                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(tierColor, 0.12), color: tierColor }}>
                                                    {tier.min_points || 0}+ pts
                                                </span>
                                            </div>
                                            {renderBenefits(tier.benefits || [])}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Earn Rules */}
                    {earnRules.length > 0 && (
                        <div className={`mt-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: visible ? `${tiers.length * 150 + 200}ms` : '0ms' }}>
                            <h3 className="text-lg font-bold text-center mb-6" style={{ color: s.text }}>
                                {content.earn_title || 'Como ganar puntos'}
                            </h3>
                            <div className={`grid grid-cols-1 sm:grid-cols-2 ${earnRules.length >= 3 ? 'lg:grid-cols-3' : ''} gap-4 max-w-3xl mx-auto`}>
                                {earnRules.map((rule, i) => (
                                    <div key={i} className="flex items-center gap-3 p-4 rounded-xl" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                            <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium" style={{ color: s.text }}>{rule.action}</span>
                                        </div>
                                        <span className="shrink-0 text-sm font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                            +{rule.points}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CTA */}
                    {content.cta_text && ctaLink && (
                        <div className={`text-center mt-10 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                            <a {...ctaLink} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 shadow-lg hover:shadow-xl" style={{ backgroundColor: s.accent }}>
                                {content.cta_text}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                            </a>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant (default fallback) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                <div className={`grid grid-cols-1 ${tiers.length === 2 ? 'md:grid-cols-2 max-w-3xl' : tiers.length >= 3 ? 'md:grid-cols-3 max-w-5xl' : 'max-w-sm'} mx-auto gap-6`}>
                    {tiers.map((tier, i) => {
                        const tierColor = tier.color || s.accent;
                        const isFirst = i === 0;
                        return (
                            <div key={i}
                                className={`relative rounded-2xl p-1 transition-all duration-700 hover:-translate-y-1 ${isFirst ? 'shadow-xl scale-[1.02]' : 'shadow-sm'} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{
                                    background: isFirst ? `linear-gradient(135deg, ${tierColor}, ${hexToRgba(tierColor, 0.7)})` : undefined,
                                    transitionDelay: visible ? `${i * 120}ms` : '0ms',
                                }}
                            >
                                {isFirst && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap" style={{ backgroundColor: tierColor }}>
                                        {content.highlight_label || 'Destacado'}
                                    </div>
                                )}
                                <div className={`rounded-xl p-6 h-full flex flex-col ${isFirst ? '' : 'border'}`}
                                    style={{
                                        backgroundColor: s.dark ? (isFirst ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)') : '#ffffff',
                                        borderColor: isFirst ? 'transparent' : (s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'),
                                    }}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        {tier.icon && <span className="text-3xl">{tier.icon}</span>}
                                        <div>
                                            <h4 className="text-lg font-bold" style={{ color: s.text }}>{tier.name}</h4>
                                            <span className="text-xs font-semibold" style={{ color: tierColor }}>
                                                {tier.min_points || 0}+ puntos
                                            </span>
                                        </div>
                                    </div>
                                    {renderBenefits(tier.benefits || [])}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Earn Rules */}
                {earnRules.length > 0 && (
                    <div className={`mt-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: visible ? `${tiers.length * 120 + 200}ms` : '0ms' }}>
                        <h3 className="text-lg font-bold text-center mb-6" style={{ color: s.text }}>
                            {content.earn_title || 'Como ganar puntos'}
                        </h3>
                        <div className={`grid grid-cols-1 sm:grid-cols-2 ${earnRules.length >= 3 ? 'lg:grid-cols-3' : ''} gap-4 max-w-3xl mx-auto`}>
                            {earnRules.map((rule, i) => (
                                <div key={i} className="flex items-center gap-3 p-4 rounded-xl" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                        <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm font-medium" style={{ color: s.text }}>{rule.action}</span>
                                    </div>
                                    <span className="shrink-0 text-sm font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                        +{rule.points}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* CTA */}
                {content.cta_text && ctaLink && (
                    <div className={`text-center mt-10 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <a {...ctaLink} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 shadow-lg hover:shadow-xl" style={{ backgroundColor: s.accent }}>
                            {content.cta_text}
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                        </a>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
